import { ethers } from 'ethers';
import { z } from 'zod';

import { MAX_THRESHOLD, THRESHOLD } from '../config/common.config';
import { ProjectFundingStatus, ProjectStageEnum, ReferralLevel, RewardType } from '../enums/project.enum';
import { ApiErrorSchema, ApiResponseSchema } from './api.schema';
import { TProjectThemeSchema } from './theme.schema';
import { VoteStatusSchema } from './vote.schema';

// Utility Validators
const numberTransformer = z.any().transform(val => (isNaN(val) ? 0 : Number(val)));
const urlPattern = /^(https?:\/\/)?([\w.-]+)\.([a-z]{2,})(:\d+)?(\/[^\s]*)?$/i;
const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const requiredString = (field: string) =>
  z
    .string({ required_error: `${field} is required` })
    .trim()
    .min(1, { message: `${field} is required` });

const urlValidator = (field: string) =>
  z
    .string({ required_error: `${field} is required` })
    .trim()
    .min(1, { message: `${field} is required` })
    .max(255, { message: 'Max characters is 255' })
    .refine(val => !val || urlPattern.test(val), {
      message: 'Please enter a valid URL',
    })
    .refine(val => !/\s/.test(val), {
      message: 'URL cannot contain spaces',
    });

const urlValidatorNotRequired = z
  .string()
  .max(255, { message: 'Max characters is 255' })
  .trim()
  .refine(val => !val || urlPattern.test(val), {
    message: 'Please enter a valid URL',
  })
  .refine(val => !/\s/.test(val), {
    message: 'URL cannot contain spaces',
  })
  .optional()
  .or(z.literal(''));

const evmAddressValidator = (field: string) =>
  requiredString(field).refine(ethers.isAddress, { message: 'Invalid EVM address' });

const percentageValidator = (min: number, max: number) =>
  z
    .number()
    .min(min, { message: `Percentage must be greater than ${min}` })
    .max(max, { message: `Percentage must be less than ${max}` });

// Decimal percentage validator (allows up to 2 decimal places)
const decimalPercentageValidator = (min: number, max: number, fieldName: string) =>
  z
    .number({ message: `${fieldName} must be a number` })
    .min(min, { message: `${fieldName} must be at least ${min}` })
    .max(max, { message: `${fieldName} must be less than ${max}` })
    .refine(val => Number.isFinite(val) && Math.round(val * 100) / 100 === val, {
      message: `${fieldName} must have at most 2 decimal places`,
    });

const rangeValidator = (min: number, max: number) =>
  numberTransformer.refine(val => val > min && val < max, { message: `Value must be between ${min} and ${max}` });

// Schemas
export const ProjectInfoSchema = z
  .object({
    url: urlValidator('Website'),
    nodeSaleUrl: urlValidatorNotRequired,
    network: z.object({
      x: urlValidatorNotRequired,
      telegram: urlValidatorNotRequired,
      discord: urlValidatorNotRequired,
      youtube: urlValidatorNotRequired,
      reddit: urlValidatorNotRequired,
      facebook: urlValidatorNotRequired,
    }),
    dns: z
      .object({
        type: z.string(),
        host: z.string(),
        value: z.string(),
      })
      .optional(),
    gAnalyticId: z
      .string()
      .regex(/^G-[A-Za-z0-9]{10}$/, { message: 'Invalid Google Analytics ID' })
      .optional()
      .or(z.literal('')),
    whitepaperUrl: urlValidatorNotRequired,
    termOfService: urlValidator('Term of Service'),
    privacyPolicy: urlValidator('Privacy Policy'),
    description: requiredString('Project Description').max(2048, { message: 'Max characters is 2048' }),
    isCustomDomain: z.boolean().optional(),
  })
  .superRefine((val, ctx) => {
    if (val.isCustomDomain) {
      if (!val.nodeSaleUrl) {
        ctx.addIssue({ path: ['nodeSaleUrl'], message: 'Core Sale URL is required', code: z.ZodIssueCode.custom });
      }
    }
  });

export const FAQSchema = z
  .array(
    z.object({
      question: requiredString('Question').max(4096),
      answer: requiredString('Answer').max(8192),
    }),
  )
  .max(100);

export const NodeSchema = z.object({
  name: requiredString('Core name')
    .min(1, { message: 'Core Name is required' })
    .min(3, { message: 'Min characters is 3' })
    .max(36, { message: 'Max characters is 36' }),
  symbol: requiredString('Core symbol').max(10),
  asset: z.array(z.any()).refine(val => val.length > 0, { message: 'NFT asset is required' }),
  description: requiredString('Core description'),
});

export const recipientSchema = z
  .array(
    z.object({
      name: requiredString('Recipient').min(3).max(36),
      address: evmAddressValidator('Recipient address'),
    }),
  )
  .max(100)
  .superRefine((val, ctx) => {
    const uniqueAddresses = new Set<string>();
    const uniqueNames = new Set<string>();
    val.forEach((recipient, index) => {
      if (uniqueAddresses.has(recipient.address)) {
        ctx.addIssue({
          path: [index, 'address'],
          message: 'Recipient address must be unique',
          code: z.ZodIssueCode.custom,
        });
      } else {
        uniqueAddresses.add(recipient.address);
      }
      if (uniqueNames.has(recipient.name)) {
        ctx.addIssue({ path: [index, 'name'], message: 'Recipient name must be unique', code: z.ZodIssueCode.custom });
      } else {
        uniqueNames.add(recipient.name);
      }
    });
  });

const phaseSchema = z
  .array(
    z.object({
      totalNode: rangeValidator(0, 2_000_000_000),
      recipients: z.array(
        z.object({
          percent: z.preprocess(
            val => (typeof val === 'string' ? parseFloat(val) : val),
            percentageValidator(0.01, 100),
          ),
          address: evmAddressValidator('Recipient'),
        }),
      ),
      referalPercent: z.number().optional(),
      isEdit: z.boolean().optional(),
      rewardType: z.any(),
      limitNodeSupply: z.any(),
    }),
  )
  .superRefine((phases, ctx) => {
    const totalNodeSoldPhases = phases.reduce((sum, phase) => sum + phase.totalNode, 0);
    if (phases.length > 100) {
      ctx.addIssue({ path: [], message: 'Maximum 100 phases are allowed', code: z.ZodIssueCode.custom });
    }
    if (phases.length === 0) {
      ctx.addIssue({ path: [], message: 'At least 1 phase is required', code: z.ZodIssueCode.custom });
    }
    (phases || []).forEach((phase, idx) => {
      const totalPercent = (phase.recipients || []).reduce((total, current) => total + current.percent, 0);
      const referralPercent = phase.referalPercent ?? 0;
      const isValidPhasePercent = 100 - referralPercent - totalPercent === 0;
      // admin able to edit phase 0 percent but input value need >= 5%
      phase.recipients?.map((recipient, recipientIndex) => {
        if (
          recipientIndex === 0 &&
          // recipient.address.toLowerCase() === WHITELIST_CREATOR_ADDRESS?.toLowerCase() &&
          recipient.percent < 5
        ) {
          ctx.addIssue({
            path: [`${idx}.recipients.${recipientIndex}.percent`],
            message: 'Percent must be greater than 5%',
            code: z.ZodIssueCode.custom,
          });
        }
        // if (
        //   recipientIndex === 0 &&
        //   // recipient.address.toLowerCase() === WHITELIST_CREATOR_ADDRESS?.toLowerCase() &&
        //   recipient.percent > 20
        // ) {
        //   ctx.addIssue({
        //     path: [`${idx}.recipients.${recipientIndex}.percent`],
        //     message: 'Percent must be less than 20%',
        //     code: z.ZodIssueCode.custom,
        //   });
        // }
      });

      if (phase.rewardType === RewardType.ERC_20) {
        if (!isValidPhasePercent)
          ctx.addIssue({
            path: [`${idx}.recipients`],
            message: `Total percentage must equal ${100 - referralPercent}% to account for the referral program allocation.`,
            code: z.ZodIssueCode.custom,
          });
        if (!phase.referalPercent) {
          ctx.addIssue({
            path: [`${idx}.recipients`],
            message: `Please input Referral Program Percentage`,
            code: z.ZodIssueCode.custom,
          });
        }
      } else {
        if (totalPercent > 100) {
          ctx.addIssue({
            path: [`${idx}.recipients`],
            message: 'Total percentage must equal 100% to account for the referral program allocation.',
            code: z.ZodIssueCode.custom,
          });
        }
      }

      if (phase.limitNodeSupply && phase.limitNodeSupply < totalNodeSoldPhases) {
        ctx.addIssue({
          path: [`${idx}.totalNode`],
          message: `Total phase core sales must match either the total Core Supply Tier or the Total Core Supply Cap in the Pricing Model. If there are remaining nodes, set up an additional phase to allocate them accordingly.`,
          code: z.ZodIssueCode.custom,
        });
      }
    });
  });

export const KolsSchema = z
  .array(
    z.object({
      address: evmAddressValidator('KOL address'),
      discount: z
        .number()
        .max(20, { message: 'Discount must be less than 20%' })
        .refine(val => val > 0, {
          message: 'Discount must be greater than 0',
        }),
    }),
  )
  .max(500)
  .superRefine((val, ctx) => {
    const uniqueAddresses = new Set<string>();
    val.forEach((kol, index) => {
      if (uniqueAddresses.has(kol.address)) {
        ctx.addIssue({
          path: [index, 'address'],
          message: 'Wallet address must be unique',
          code: z.ZodIssueCode.custom,
        });
      } else {
        uniqueAddresses.add(kol.address);
      }
    });
  });

export const ReferralSchema = z
  .object({
    isEnable: z.boolean(),
    isEnableDonation: z.boolean(),
    isEnableDiscountAll: z.boolean(),
    isEnableDiscountKol: z.boolean(),
    isEnableDiscount: z.boolean(),
    allocatedAddress: evmAddressValidator('Allocated address'),
    rewardDescription: z.string().trim().min(1, { message: `Description is required` }).optional(),
    rewardType: z.enum([RewardType.ERC_20, RewardType.NFT_NODE]),
    percentage: decimalPercentageValidator(1, 20, 'Percentage').default(0),
    rewardLv: z
      .array(
        z.object({
          node: z.number({ message: 'Please input a number' }),
        }),
      )
      .superRefine((val, ctx) => {
        val.forEach((reward, index) => {
          if (index > 0 && reward.node && val?.[index - 1] && reward.node <= (val?.[index - 1]?.node || 0)) {
            ctx.addIssue({
              path: [index, 'node'],
              message: 'Node amount must be greater than previous level',
              code: z.ZodIssueCode.custom,
            });
          }
        });
      }),
    referralLv: z.enum([ReferralLevel.LEVEL_1, ReferralLevel.LEVEL_2]),
    referLv1: decimalPercentageValidator(1, 100, 'Level 1 percentage').default(0),
    referLv2: decimalPercentageValidator(0, 100, 'Level 2 percentage').default(0),
    donation: z
      .number({ message: 'Donation is required' })
      .int({ message: 'Donation must be a whole number' })
      .optional(),
    discount: z
      .number({ message: 'Enter a valid number' })
      .int({ message: 'Discount must be a whole number' })
      .max(20, { message: 'Discount must be less than 20%' })
      .optional(),
  })
  .superRefine((val, ctx) => {
    const referLv1 = val.referLv1 || 0;
    const referLv2 = val.referLv2 || 0;
    const totalReferLv = referLv1 + referLv2;
    const isFirstLevelReferral = val.referralLv === ReferralLevel.LEVEL_1;

    if (!referLv1 && !isFirstLevelReferral) {
      ctx.addIssue({
        path: ['referLv1'],
        message: 'Percentage is required',
        code: z.ZodIssueCode.custom,
      });
    }

    if (!referLv2 && val.referralLv === ReferralLevel.LEVEL_2) {
      ctx.addIssue({
        path: ['referLv2'],
        message: 'Percentage is required',
        code: z.ZodIssueCode.custom,
      });
    }

    if (val.referralLv === ReferralLevel.LEVEL_1 && val.percentage !== undefined && referLv1 > val.percentage) {
      ctx.addIssue({
        path: ['referLv1'],
        message: 'Percentage levels 1 can not exceed Percentage above',
        code: z.ZodIssueCode.custom,
      });
    }

    // Add validation for referLv1 > referLv2
    if (val.referralLv === ReferralLevel.LEVEL_2 && referLv1 <= referLv2 && referLv2 > 0) {
      ctx.addIssue({
        path: ['referLv1'],
        message: 'Level 1 percentage must be greater than Level 2 percentage',
        code: z.ZodIssueCode.custom,
      });
      ctx.addIssue({
        path: ['referLv2'],
        message: 'Level 2 percentage must be less than Level 1 percentage',
        code: z.ZodIssueCode.custom,
      });
    }

    if (
      val.referralLv === ReferralLevel.LEVEL_2 &&
      val.percentage !== undefined &&
      val.percentage !== 0 &&
      totalReferLv > val.percentage
    ) {
      ctx.addIssue({
        path: ['referLv1'],
        message:
          'The combined percentage of two-level referrals cannot exceed the total allocated referral percentage.',
        code: z.ZodIssueCode.custom,
      });
      ctx.addIssue({
        path: ['referLv2'],
        message:
          'The combined percentage of two-level referrals cannot exceed the total allocated referral percentage.',
        code: z.ZodIssueCode.custom,
      });
    }

    if (val.isEnableDonation) {
      if (!val.donation) {
        ctx.addIssue({ path: ['donation'], message: 'Donation is required', code: z.ZodIssueCode.custom });
      }

      const amountDonation = Number(val.donation);
      if (amountDonation < 1 || amountDonation > 20) {
        ctx.addIssue({
          path: ['donation'],
          message: 'Donation must be greater than 1% and less than 20%.',
          code: z.ZodIssueCode.custom,
        });
      }
    }
  });

export const SettingSchema = z
  .object({
    startedAt: z.union([
      z.date({ required_error: 'Date is required' }),
      z.string().refine(val => !isNaN(Date.parse(val)), { message: 'Invalid date string format' }),
    ]),
    lockTransfer: z.number({ message: 'Lock Transer is required' }).min(0).max(365),
    maxPurchaseTx: z.number({ message: 'Limit is required' }).min(1, { message: 'Limit is required' }).max(32_000),
    maxPurchaseWl: z
      .number({ message: 'Limit is required' })
      .min(1, { message: 'Limit is required' })
      .max(2_000_000_000),
    defaultToken: z.string(),
  })
  .superRefine((val, ctx) => {
    if (val.startedAt) {
      if (new Date(val.startedAt).getTime() < new Date().getTime()) {
        ctx.addIssue({ path: ['startedAt'], message: 'Start date must be in the future', code: z.ZodIssueCode.custom });
      }
    }
    if (val.maxPurchaseTx && val.maxPurchaseWl && val.maxPurchaseTx > val.maxPurchaseWl) {
      ctx.addIssue({
        path: ['maxPurchaseTx'],
        message: 'Max purchase tx must be less than max purchase per wallet',
        code: z.ZodIssueCode.custom,
      });
    }
  });

export const GroupSchema = z
  .array(
    z
      .object({
        name: requiredString('Group name').max(64),
        openingAt: z.union([
          z.date({ required_error: 'Date is required' }),
          z.string().refine(val => !isNaN(Date.parse(val)), { message: 'Invalid date string format' }),
        ]),
        closingAt: z.union([
          z.date({ required_error: 'Date is required' }),
          z.string().refine(val => !isNaN(Date.parse(val)), { message: 'Invalid date string format' }),
        ]),
        whitelist: z
          .array(evmAddressValidator('Whitelist address'))
          .max(500)
          .superRefine((val, ctx) => {
            if (val.length === 0) {
              ctx.addIssue({
                path: ['whitelist'],
                message: 'At least 1 whitelist is required',
                code: z.ZodIssueCode.custom,
              });
            }
            const uniqueAddresses = new Set<string>();
            val.forEach((address, index) => {
              if (uniqueAddresses.has(address)) {
                ctx.addIssue({
                  path: [index],
                  message: 'Whitelist address must be unique',
                  code: z.ZodIssueCode.custom,
                });
              }
            });
          }),
      })
      .superRefine((val, ctx) => {
        if (new Date(val.openingAt).getTime() >= new Date(val.closingAt).getTime()) {
          ctx.addIssue({
            path: ['openingAt'],
            message: 'Start date must be before closing date',
            code: z.ZodIssueCode.custom,
          });
        }
      }),
  )
  .superRefine((val, ctx) => {
    const uniqueNames = new Set<string>();
    val.forEach((group, index) => {
      if (uniqueNames.has(group.name)) {
        ctx.addIssue({ path: [index, 'name'], message: 'Group name must be unique', code: z.ZodIssueCode.custom });
      } else {
        uniqueNames.add(group.name);
      }
    });
  });

export const TierSchema = z
  .object({
    layer: z
      .array(
        z.object({
          total: rangeValidator(0, 2_000_000_000),
          price: numberTransformer.refine(val => val >= 1 && val < 2_000_000_000, {
            message: `Value must be between 1 and 2,000,000,000`,
          }),
        }),
      )
      .superRefine((val, ctx) => {
        if (val.length > 1000) {
          ctx.addIssue({ path: [], message: 'Maximum 1000 tiers are allowed', code: z.ZodIssueCode.custom });
        }
        if (val.length === 0) {
          ctx.addIssue({ path: [], message: 'At least 1 tier is required', code: z.ZodIssueCode.custom });
        }
        val.forEach((tier, index) => {
          if (index > 0 && tier.price < val[index - 1].price) {
            ctx.addIssue({
              path: [index, 'price'],
              message: 'Price must be greater than previous tier price',
              code: z.ZodIssueCode.custom,
            });
          }
        });
      }),
    supplyIn: numberTransformer,
    pricePer: numberTransformer,
    nodeSupplyCap: numberTransformer,
    priceCap: numberTransformer,
    autoAdjustment: z.boolean(),
  })
  .superRefine((val, ctx) => {
    if (val.autoAdjustment) {
      if (!val.supplyIn) {
        ctx.addIssue({ path: ['supplyIn'], message: 'Supply in is required', code: z.ZodIssueCode.custom });
      }
      if (!val.pricePer) {
        ctx.addIssue({ path: ['pricePer'], message: 'Price per is required', code: z.ZodIssueCode.custom });
      }
      if (!val.nodeSupplyCap) {
        ctx.addIssue({
          path: ['nodeSupplyCap'],
          message: 'Total Core supply cap is required',
          code: z.ZodIssueCode.custom,
        });
      }
      if (!val.priceCap) {
        ctx.addIssue({ path: ['priceCap'], message: 'Price cap is required', code: z.ZodIssueCode.custom });
      }
      const totalSupply = val.layer.reduce((sum, tier) => sum + tier.total, 0);
      if (val.nodeSupplyCap && val.nodeSupplyCap < totalSupply + val.supplyIn) {
        // nodeSupplyCap must be greater than totalSupply + supplyIn (one more tier)
        ctx.addIssue({
          path: ['nodeSupplyCap'],
          message: 'The Total Core supply must be greater than the current total core supply and supply in tier',
          code: z.ZodIssueCode.custom,
        });
      }
      if (val.pricePer && val.priceCap <= (val.layer?.[val.layer.length - 1]?.price || 0) + val.pricePer) {
        ctx.addIssue({
          path: ['priceCap'],
          message: 'Price cap must be greater than total price',
          code: z.ZodIssueCode.custom,
        });
      }
    }
  });

export const RoyaltySchema = z
  .array(
    z.object({
      percent: z.number({ message: 'Percentage is required' }).min(1, { message: 'Percentage is required' }),
      address: z
        .string({ message: 'Address is required' })
        .min(1, { message: 'Address is required' })
        .refine(ethers.isAddress, { message: 'Invalid EVM address' }),
    }),
  )
  .superRefine((val, ctx) => {
    const totalPercent = val.reduce((total, cur) => total + cur.percent, 0);
    const uniqueDuplicates = new Set<string>();
    val.forEach((v, i) => {
      if (totalPercent > 10) {
        ctx.addIssue({
          path: [i, `percent`],
          message: 'Total Percentages can not exceed 10',
          code: z.ZodIssueCode.custom,
        });
      }
      if (uniqueDuplicates.has(v.address)) {
        ctx.addIssue({
          path: [i, `address`],
          message: 'The address cannot be duplicated',
          code: z.ZodIssueCode.custom,
        });
      } else {
        uniqueDuplicates.add(v.address);
      }
    });
  });

export const ThresholdSchema = z.object({
  threshold: z
    .number({ message: 'Enter a valid number' })
    .min(Number(THRESHOLD), { message: `Sales Threshold must be greater than ${THRESHOLD}` })
    .max(Number(MAX_THRESHOLD), { message: `Sales Threshold must be less than ${MAX_THRESHOLD}` })
    .transform(val => (isNaN(val) ? 0 : Number(val))),
  timeframe: z.number(),
});

export const NodeSystemSchema = z
  .object({
    isDatagramEnabled: z.boolean(),
    email: z
      .string()
      .optional()
      .refine(val => !val || emailPattern.test(val), {
        message: 'Enter a valid email address',
      }),
  })
  .superRefine((val, ctx) => {
    if (val.isDatagramEnabled && !val.email) {
      ctx.addIssue({ path: ['email'], message: 'Email is required', code: z.ZodIssueCode.custom });
    }
  });

export const ProjectInputSchema = z.object({
  name: z
    .string()
    .min(1, { message: 'Project Name is required' })
    .min(3, { message: 'Min characters is 3' })
    .max(36, { message: 'Max characters is 36' }),
  info: ProjectInfoSchema,
  faqs: FAQSchema.optional(),
  node: NodeSchema,
  recipients: recipientSchema,
  phases: phaseSchema,
  referral: ReferralSchema,
  kols: KolsSchema,
  setting: SettingSchema,
  tier: TierSchema,
  royalties: RoyaltySchema,
  threshold: ThresholdSchema,
  nodeSystem: NodeSystemSchema,
});

export const ProjectReferralCreateSchema = z.object({
  isEnable: z.boolean(),
  percentage: z.number().optional(),
  rewardDescription: z.string(),
  redeemDescription: z.string().optional(),
  referralLv: z.enum([ReferralLevel.LEVEL_1, ReferralLevel.LEVEL_2]).optional(),
  rewardType: z.enum([RewardType.ERC_20, RewardType.NFT_NODE]),
  rewardLv: z.array(z.number()),
  referLv1: z.number().optional(),
  referLv2: z.number().optional(),
  donation: z.number().optional(),
  discount: z.number().optional(),
  isEnableReward: z.boolean(),
  isEnableRedeem: z.boolean(),
  isEnableDonation: z.boolean(),
  isEnableDiscount: z.boolean(),
  isEnableDiscountAll: z.boolean(),
  isEnableDiscountKol: z.boolean(),
});

export const ProjectSettingCreateSchema = z.object({
  updatedAt: z.date(),
  startedAt: z.union([z.date(), z.string()]),
  lockTransfer: z.number(),
  maxPurchaseTx: z.number(),
  maxPurchaseWl: z.number(),
  defaultToken: z.string(),
});

export const CreateProjectDTOSchema = z.object({
  creator: z.string(),
  name: z.string(),
  info: ProjectInfoSchema,
  faqs: FAQSchema,
  node: z.object({
    name: z.string(),
    symbol: z.string(),
    asset: z.string(),
    description: z.string(),
  }),
  phases: phaseSchema,
  recipients: recipientSchema,
  referral: ProjectReferralCreateSchema,
  kols: KolsSchema,
  setting: ProjectSettingCreateSchema,
  tier: z.object({
    layer: z.array(
      z.object({
        total: z.number(),
        price: numberTransformer.refine(val => val >= 1 && val < 2_000_000_000, {
          message: `Value must be between 1 and 2,000,000,000`,
        }),
      }),
    ),
    supplyIn: z.number(),
    pricePer: z.number(),
    nodeSupplyCap: z.number(),
    priceCap: z.number(),
    autoAdjustment: z.boolean(),
  }),
  royalties: z.array(
    z.object({
      percent: percentageValidator(0, 100),
      address: evmAddressValidator('Royalty address'),
    }),
  ),
  theme: TProjectThemeSchema,
  threshold: ThresholdSchema,
  nodeSystem: NodeSystemSchema,
});

export const ProjectListItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  state: z.enum([
    ProjectStageEnum.DRAFT,
    ProjectStageEnum.STAGING,
    ProjectStageEnum.PRODUCTION,
    ProjectStageEnum.VOTING,
  ]),
  url: z.string().optional(), // STAGING
  nodeSaleUrl: z.string().optional(), // STAGING
  createdBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  publishAt: z.string().optional(), // DRAFT -> STAGING
  requestAt: z.string().optional(), // STAGING -> VOTING
  releaseAt: z.string().optional(), // VOTING -> PRODUCTION
  votes: z.array(VoteStatusSchema),
  setting: z
    .object({
      currentTier: z.string(),
      fundingStatus: z.enum([ProjectFundingStatus.LOCK, ProjectFundingStatus.UNLOCK, ProjectFundingStatus.REFUND]),
      saleStartDate: z.string(),
      totalSold: z.number(),
      totalSupply: z.number(),
    })
    .optional(),
});

export const ProjectListSchema = ApiResponseSchema.extend({
  data: z.array(ProjectListItemSchema),
});

export const ProjectDetailSchema = CreateProjectDTOSchema.extend({
  addr: z.string(),
  id: z.string(),
  state: z.enum([
    ProjectStageEnum.DRAFT,
    ProjectStageEnum.STAGING,
    ProjectStageEnum.PRODUCTION,
    ProjectStageEnum.VOTING,
  ]),
});

export const ProjectDetailResponseSchema = ApiResponseSchema.extend({
  data: ProjectDetailSchema,
}).merge(ApiErrorSchema);

export const ProjectAdminWalletSchema = z.object({
  addr: z.string(),
  minPercent: z.number(),
  maxPercent: z.number(),
  type: z.string(),
  name: z.string(),
});

export const ProjectAdminWalletResponseSchema = ApiResponseSchema.extend({
  data: ProjectAdminWalletSchema,
}).merge(ApiErrorSchema);

export const VerifyDNSSchema = z.object({
  name: z.string(),
  apexName: z.string(),
  projectId: z.string(),
  redirect: z.string().optional(),
  redirectStatusCode: z.number().optional(),
  gitBranch: z.string().optional(),
  customEnvironmentId: z.string().optional(),
  updatedAt: z.number(),
  createdAt: z.number(),
  verified: z.boolean(),
  verification: z
    .array(
      z.object({
        type: z.string(),
        domain: z.string(),
        value: z.string(),
        reason: z.string().optional(),
      }),
    )
    .optional(),
});

export const ValidateDomainSchema = z.object({
  isValid: z.boolean(),
  isDomainVerified: z.boolean(),
  data: VerifyDNSSchema,
});

export const ValidateDomainResponseSchema = ApiResponseSchema.extend({
  data: ValidateDomainSchema,
}).merge(ApiErrorSchema);

export type CreateProjectDTOType = z.infer<typeof CreateProjectDTOSchema>;
export type ProjectListItem = z.infer<typeof ProjectListItemSchema>;
export type ProjectListResponseType = z.infer<typeof ProjectListSchema>;
export type ProjectDetailResponseType = z.infer<typeof ProjectDetailResponseSchema>;
export type ProjectDetailType = z.infer<typeof ProjectDetailSchema>;
export type ProjectUpdatePayloadType = {
  id: string;
  data: DeepPartial<CreateProjectDTOType>;
  preCheck?: boolean;
};

export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};
export type ThresholdType = z.infer<typeof ThresholdSchema>;
export type ProjectAdminWalletType = z.infer<typeof ProjectAdminWalletSchema>;
export type ProjectAdminWalletResponseType = z.infer<typeof ProjectAdminWalletResponseSchema>;
export type ValidateDomainType = z.infer<typeof ValidateDomainSchema>;
export type ValidateDomainResponseType = z.infer<typeof ValidateDomainResponseSchema>;
export type VerifyDNSType = z.infer<typeof VerifyDNSSchema>;

export type ReferralSchemaType = z.infer<typeof ReferralSchema>;
export type ProjectInputSchemaType = z.infer<typeof ProjectInputSchema>;
export type NodeSchemaType = z.infer<typeof NodeSchema>;
export type NodeSystemSchemaType = z.infer<typeof NodeSystemSchema>;

// Production-specific schema - only validates fields that can be updated in PRODUCTION status
export const ProjectInputProductionSchema = z.object({
  name: z
    .string()
    .min(1, { message: 'Project Name is required' })
    .min(3, { message: 'Min characters is 3' })
    .max(36, { message: 'Max characters is 36' }),
  info: z.object({
    url: urlValidator('Website'),
    description: requiredString('Project Description'),
    whitepaperUrl: urlValidatorNotRequired,
    termOfService: urlValidator('Term of Service'),
    privacyPolicy: urlValidator('Privacy Policy'),
    gAnalyticId: z
      .string()
      .regex(/^G-[A-Za-z0-9]{10}$/, { message: 'Invalid Google Analytics ID' })
      .optional()
      .or(z.literal('')),
    network: z.object({
      x: urlValidatorNotRequired,
      telegram: urlValidatorNotRequired,
      discord: urlValidatorNotRequired,
      youtube: urlValidatorNotRequired,
      reddit: urlValidatorNotRequired,
      facebook: urlValidatorNotRequired,
    }),
    // Other fields from info should be ignored in production validation
    nodeSaleUrl: z.any().optional(),
    dns: z.any().optional(),
    isCustomDomain: z.any().optional(),
  }),
  faqs: FAQSchema.optional(),
  setting: z
    .object({
      maxPurchaseWl: z
        .number({ message: 'Limit is required' })
        .min(1, { message: 'Limit is required' })
        .max(2_000_000_000),
      maxPurchaseTx: z.number({ message: 'Limit is required' }).min(1, { message: 'Limit is required' }).max(32_000),
      defaultToken: z.string(),
      // Other fields from setting should be ignored in production validation
      startedAt: z.any().optional(),
      lockTransfer: z.any().optional(),
    })
    .superRefine((val, ctx) => {
      if (val.maxPurchaseTx && val.maxPurchaseWl && val.maxPurchaseTx > val.maxPurchaseWl) {
        ctx.addIssue({
          path: ['maxPurchaseTx'],
          message: 'Max purchase tx must be less than max purchase per wallet',
          code: z.ZodIssueCode.custom,
        });
      }
    }),
  node: NodeSchema,
  referral: z.object({
    rewardDescription: z.string().trim().min(1, { message: `Description is required` }).optional(),
    // Other fields from referral should be ignored in production validation
    isEnable: z.any().optional(),
    isEnableDonation: z.any().optional(),
    isEnableDiscountAll: z.any().optional(),
    isEnableDiscountKol: z.any().optional(),
    isEnableDiscount: z.any().optional(),
    allocatedAddress: z.any().optional(),
    rewardType: z.any().optional(),
    percentage: z.any().optional(),
    rewardLv: z.any().optional(),
    referralLv: z.any().optional(),
    referLv1: z.any().optional(),
    referLv2: z.any().optional(),
    donation: z.any().optional(),
    discount: z.any().optional(),
  }),
  nodeSystem: z.object({
    email: z
      .string()
      .optional()
      .refine(val => !val || emailPattern.test(val), {
        message: 'Enter a valid email address',
      }),
    // Other fields from nodeSystem should be ignored in production validation
    isDatagramEnabled: z.any().optional(),
  }),
  // These fields are not editable in production but should be present to avoid form errors
  recipients: z.any().optional(),
  phases: z.any().optional(),
  kols: z.any().optional(),
  tier: z.any().optional(),
  royalties: z.any().optional(),
  threshold: z.any().optional(),
});

export type ProjectInputProductionSchemaType = z.infer<typeof ProjectInputProductionSchema>;
